import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { formatPrice, formatHour, SPORT_COLORS, hexToRgba } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import BackButton from "@/components/BackButton";
import useSocket from '../hooks/useSocket';
import { Clock, Users, Activity, MapPin, ShieldCheck, CheckCircle, X, UserPlus, MessageSquare, Star, Receipt, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function PlaySessionDetail() {
  const { t } = useTranslation();
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joinCount, setJoinCount] = useState(1);
  const [actionLoading, setActionLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [ratings, setRatings] = useState({});
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [existingRatings, setExistingRatings] = useState([]);
  const ratingsSectionRef = useRef(null);

  const load = () => {
    api.get(`/play/${sessionId}`).then((r) => setSession(r.data)).catch(() => navigate("/play", { replace: true })).finally(() => setLoading(false));
  };

  useEffect(load, [sessionId, navigate]);

  useEffect(() => {
    if (session && user) {
      api.get(`/play/${sessionId}/ratings`).then(r => setExistingRatings(r.data)).catch((e) => {
        if (import.meta.env.DEV) console.warn("play ratings fetch failed:", e);
      });
    }
  }, [session?.id, user]);

  const handleJoin = async () => {
    if (!user) { navigate(`/login?redirect=/play/${sessionId}`); return; }
    setActionLoading(true);
    try {
      await api.post(`/play/${sessionId}/claim`, { player_count: joinCount });
      toast.success(t("play.joined"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActionLoading(false); }
  };

  const handleLeave = async () => {
    setActionLoading(true);
    try {
      await api.delete(`/play/${sessionId}/claim`);
      toast.success(t("play.left"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActionLoading(false); }
  };

  const handleConfirm = async () => {
    setActionLoading(true);
    try {
      await api.post(`/play/${sessionId}/confirm`);
      toast.success(t("play.confirmed_toast"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActionLoading(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm(t("play.cancel_confirm"))) return;
    setActionLoading(true);
    try {
      await api.post(`/play/${sessionId}/cancel`);
      toast.success(t("play.cancelled_toast"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActionLoading(false); }
  };

  const handleRateSubmit = async () => {
    const ratingsArray = Object.entries(ratings).map(([toUserId, vals]) => ({
      to_user_id: toUserId,
      punctuality: vals.punctuality || 3,
      skill: vals.skill || 3,
      attitude: vals.attitude || 3,
    }));
    if (ratingsArray.length === 0) return;
    setRatingsLoading(true);
    try {
      await api.post(`/play/${sessionId}/rate`, { ratings: ratingsArray });
      toast.success(t("play.ratings_submitted"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRatingsLoading(false); }
  };

  const handleChatToggle = () => setShowChat(!showChat);

  useEffect(() => {
    if (!session) return;
    const nextRemaining = session.max_players - session.filled_count;
    const nextMinJoinCount = Math.max(1, session.min_players_per_claim || 1);
    setJoinCount((current) => {
      const safeCurrent = Number.isFinite(current) ? current : nextMinJoinCount;
      return Math.min(
        Math.max(safeCurrent, nextMinJoinCount),
        Math.max(nextMinJoinCount, nextRemaining || nextMinJoinCount)
      );
    });
  }, [session]);

  useEffect(() => {
    if (!session || !searchParams.get("rate")) return;
    const isHost = user?.id === session.host_id;
    const myClaim = session.claims?.find((c) => c.captain_id === user?.id);
    const isConfirmed = session.status === "confirmed";
    const isCompleted = session.status === "completed";
    const sessionEnd = new Date(session.date);
    sessionEnd.setHours(session.end_hour, 0, 0, 0);
    const isPast = new Date() >= sessionEnd;
    if (!(isConfirmed || isCompleted) || !isPast || !myClaim || isHost) return;
    setTimeout(() => {
      ratingsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }, [searchParams, session, user]);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-background flex items-center justify-center">
        <div className="label-eyebrow animate-pulse">{t("common.loading")}</div>
      </div>
    );
  }

  if (!session) return null;

  const remaining = session.max_players - session.filled_count;
  const isHost = user?.id === session.host_id;
  const myClaim = session.claims?.find((c) => c.captain_id === user?.id);
  const isConfirmed = session.status === "confirmed";
  const now = new Date();
  const sessionEnd = new Date(session.date);
  sessionEnd.setHours(session.end_hour, 0, 0, 0);
  const isPast = now >= sessionEnd;
  const isOpen = session.status === "open";
  const isCompleted = session.status === "completed";
  const minJoinCount = Math.max(1, session.min_players_per_claim || 1);
  const canMeetMinimumClaim = remaining >= minJoinCount;
  const paidClaims = session.claims?.filter((c) => (c.paid_amount || 0) > 0) || [];
  const totalPaid = paidClaims.reduce((sum, claim) => sum + (claim.paid_amount || 0), 0);
  const myPaidAmount = myClaim?.paid_amount || 0;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-2xl mx-auto px-5 md:px-10 py-10">
        <BackButton className="mb-8" />

        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-10 h-10 flex items-center justify-center"
            style={{ background: hexToRgba(SPORT_COLORS[session.sport]?.bg, 0.15) }}
          >
            <Activity className="h-5 w-5" style={{ color: SPORT_COLORS[session.sport]?.bg }} />
          </div>
          <div className="label-eyebrow text-volt">{t("home.sports." + session.sport)}</div>
        </div>

        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-2">{session.court_name}</h1>
        <p className="text-sm text-muted-foreground mb-8">
          {session.date} · {formatHour(session.start_hour)} – {formatHour(session.end_hour)}
        </p>

        {/* Status & details grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="border border-border/30 bg-card p-4">
            <Users className="h-4 w-4 text-muted-foreground mb-1" />
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-0.5">{t("play.players")}</div>
            <div className="font-heading text-3xl text-volt">{session.filled_count}<span className="text-lg text-muted-foreground/60">/{session.max_players}</span></div>
          </div>
          <div className="border border-border/30 bg-card p-4">
            <Clock className="h-4 w-4 text-muted-foreground mb-1" />
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-0.5">{t("play.duration")}</div>
            <div className="font-semibold text-sm">{session.end_hour - session.start_hour}h</div>
          </div>
          <div className="border border-border/30 bg-card p-4">
            <ShieldCheck className="h-4 w-4 text-muted-foreground mb-1" />
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-0.5">{t("play.skill_level")}</div>
            <div className="font-semibold text-sm">{t("play.level_" + session.skill_level)}</div>
          </div>
          <div className="border border-border/30 bg-card p-4">
            <MapPin className="h-4 w-4 text-muted-foreground mb-1" />
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-0.5">{t("play.per_player")}</div>
            <div className="font-heading text-3xl text-volt">{formatPrice(session.price_per_player, session.currency)}</div>
          </div>
        </div>

        {/* Claims list */}
        <div className="border border-border bg-card p-5 mb-6">
          <div className="label-eyebrow mb-4">
            {t("play.claims")} ({session.claims?.length || 0})
          </div>
          {session.claims?.length === 0 ? (
            <p className="text-sm text-muted-foreground/60 text-center py-6">{t("play.no_claims")}</p>
          ) : (
            <div className="space-y-2">
              {session.claims.map((c, i) => (
                <div key={i} className="flex items-center justify-between border border-border/30 bg-background p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-volt/10 text-volt flex items-center justify-center font-bold text-xs">
                      {c.captain_name?.slice(0, 1) || "?"}
                    </div>
                    <div>
                      {c.captain_id ? (
                        <Link to={`/player/${c.captain_id}`} className="text-sm font-semibold hover:text-volt transition-colors">
                          {c.captain_name}
                        </Link>
                      ) : (
                        <div className="text-sm font-semibold">{c.captain_name}</div>
                      )}
                      <div className="text-[10px] text-muted-foreground">
                        {c.type === "team" ? t("play.team_claim") : t("play.solo_claim")} · {c.player_count} {t("play.player", { count: c.player_count })}
                      </div>
                    </div>
                  </div>
                  <span className={`text-[9px] tracking-widest uppercase border px-1.5 py-0.5 ${
                    c.status === "confirmed" ? "border-emerald-400/30 text-emerald-400" : "border-yellow-400/30 text-yellow-400"
                  }`}>
                    {c.status === "confirmed" ? t("play.confirmed") : t("play.pending")}
                  </span>
                    {c.paid_amount > 0 && (
                      <span className="text-[9px] tracking-widest uppercase text-volt ms-2">
                        {formatPrice(c.paid_amount, session.currency)}
                      </span>
                    )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {isOpen && isPast && (
            <div className="border border-border bg-card p-5 text-center">
              <Clock className="h-8 w-8 text-muted-foreground/60 mx-auto mb-2" />
              <p className="font-heading text-2xl text-muted-foreground/80">{t("play.session_ended")}</p>
              <p className="text-sm text-muted-foreground/60 mt-2">{t("play.session_ended_hint")}</p>
            </div>
          )}

          {isOpen && !isPast && remaining > 0 && !myClaim && canMeetMinimumClaim && (
            <div className="border border-border bg-card p-5">
              <div className="label-eyebrow mb-3">{t("play.join_title")}</div>
              <div className="flex items-center gap-3 mb-3">
                <label className="text-xs text-muted-foreground">{t("play.players_count")}</label>
                <input
                  type="number"
                  min={minJoinCount}
                  max={remaining}
                  value={joinCount}
                  onChange={(e) => {
                    const next = parseInt(e.target.value, 10);
                    const safeValue = Number.isNaN(next) ? minJoinCount : next;
                    setJoinCount(Math.min(Math.max(safeValue, minJoinCount), remaining));
                  }}
                  className="w-20 bg-background border border-input px-3 py-2 text-sm text-center focus:border-volt focus:outline-none"
                />
                <span className="text-[10px] text-muted-foreground">{remaining} {t("play.spots_left")}</span>
              </div>
              <p className="text-[10px] text-muted-foreground/70 mb-3">
                {t("play.join_min_hint", { n: minJoinCount })}
              </p>
              <button
                onClick={handleJoin}
                disabled={actionLoading}
                className="btn-volt rounded-sm px-6 py-3 text-xs flex items-center gap-2 disabled:opacity-40"
              >
                <UserPlus className="h-4 w-4" /> {actionLoading ? t("common.loading") : t("play.join")}
              </button>
            </div>
          )}

          {isOpen && !isPast && remaining > 0 && !myClaim && !canMeetMinimumClaim && (
            <div className="border border-border bg-card p-5 text-center">
              <Users className="h-8 w-8 text-muted-foreground/60 mx-auto mb-2" />
              <p className="font-heading text-2xl text-muted-foreground/80">{t("play.join_not_available")}</p>
              <p className="text-sm text-muted-foreground/60 mt-2">
                {t("play.join_not_available_hint", { min: minJoinCount, remaining })}
              </p>
            </div>
          )}

          {myClaim && session.status === "open" && (
            <div className="border border-yellow-400/20 bg-yellow-400/5 p-5">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-yellow-400" />
                <span className="text-sm font-semibold text-yellow-400">{t("play.you_claimed")}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{myClaim.player_count} {t("play.player", { count: myClaim.player_count })}</p>
              {!isHost && (
                <button
                  onClick={handleLeave}
                  disabled={actionLoading}
                  className="btn-outline rounded-sm px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-40"
                >
                  <X className="h-3 w-3" /> {actionLoading ? t("common.loading") : t("play.leave")}
                </button>
              )}
            </div>
          )}

          {isHost && session.status === "open" && (
            <div className="flex gap-3">
              <button
                onClick={handleConfirm}
                disabled={remaining > 0 || actionLoading}
                className={`rounded-sm px-6 py-3 text-xs flex items-center gap-2 flex-1 justify-center font-bold disabled:opacity-40 ${
                  remaining === 0 ? "bg-volt text-primary-foreground" : "border border-yellow-400/30 text-yellow-400 bg-yellow-400/5"
                }`}
              >
                <CheckCircle className="h-4 w-4" />
                {remaining > 0
                  ? t("play.waiting_for", { n: remaining })
                  : actionLoading ? t("common.loading") : t("play.confirm_session")}
              </button>
              <button
                onClick={handleCancel}
                disabled={actionLoading}
                className="btn-outline rounded-sm px-4 py-3 text-xs disabled:opacity-40"
              >
                {t("play.cancel")}
              </button>
            </div>
          )}

          {isConfirmed && (
            <div className="border border-emerald-400/20 bg-emerald-400/5 p-5">
              <div className="text-center">
                <CheckCircle className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                <p className="font-heading text-2xl text-emerald-400 mb-1">{t("play.session_confirmed")}</p>
                <p className="text-sm text-muted-foreground mb-5">{t("play.session_confirmed_hint")}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                {session.booking_id && (
                  <button
                    onClick={() => navigate(`/bookings/${session.booking_id}`)}
                    className="btn-volt rounded-sm px-5 py-3 text-xs inline-flex items-center justify-center gap-2"
                  >
                    <Receipt className="h-4 w-4" /> {t("play.open_booking")}
                  </button>
                )}
                {session.chat_id && (
                  <button
                    onClick={handleChatToggle}
                    className="btn-outline rounded-sm px-5 py-3 text-xs inline-flex items-center justify-center gap-2"
                  >
                    <MessageSquare className="h-4 w-4" /> {showChat ? t("play.hide_team_chat") : t("play.open_team_chat")}
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="border border-border/30 bg-card p-4">
                  <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1">
                    {isHost ? t("play.total_collected") : t("play.your_payment")}
                  </div>
                  <div className="font-heading text-2xl text-volt">
                    {formatPrice(isHost ? totalPaid : myPaidAmount, session.currency)}
                  </div>
                  <div className="text-[11px] text-muted-foreground/70 mt-1">
                    {isHost
                      ? t("play.total_collected_hint", { count: paidClaims.length, total: session.claims?.length || 0 })
                      : t("play.your_payment_hint")}
                  </div>
                </div>
                <div className="border border-border/30 bg-card p-4">
                  <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1">
                    {t("play.next_step")}
                  </div>
                  <div className="font-semibold text-sm">
                    {session.chat_id ? t("play.next_step_chat") : t("play.next_step_booking")}
                  </div>
                  <div className="text-[11px] text-muted-foreground/70 mt-1 inline-flex items-center gap-1">
                    <ArrowRight className="h-3.5 w-3.5" />
                    {session.chat_id ? t("play.next_step_chat_hint") : t("play.next_step_booking_hint")}
                  </div>
                </div>
              </div>
            </div>
          )}

          {session.status === "cancelled" && (
            <div className="border border-red-400/20 bg-red-400/5 p-5 text-center">
              <X className="h-8 w-8 text-red-400 mx-auto mb-2" />
              <p className="font-heading text-2xl text-red-400">{t("play.session_cancelled")}</p>
            </div>
          )}

          {(isConfirmed || isCompleted) && isPast && myClaim && !isHost && (
            <div ref={ratingsSectionRef} className="border border-border bg-card p-5">
              <div className="label-eyebrow mb-4">{t("play.rate_players")}</div>
              {session.claims.filter(c => c.captain_id !== user?.id).length === 0 ? (
                <p className="text-sm text-muted-foreground/60">{t("play.no_claims")}</p>
              ) : (
                <div className="space-y-3">
                  {session.claims.filter(c => c.captain_id !== user?.id).map((c) => {
                    const alreadyRated = existingRatings.some(r => r.from_user_id === user?.id && r.to_user_id === c.captain_id);
                    if (alreadyRated) return (
                      <div key={c.captain_id} className="flex items-center justify-between border border-border/30 bg-background p-3">
                        <span className="text-sm">{c.captain_name}</span>
                        <span className="text-[10px] text-muted-foreground">{t("play.already_rated")}</span>
                      </div>
                    );
                    return (
                      <div key={c.captain_id} className="border border-border/30 bg-background p-3">
                        <div className="text-sm font-semibold mb-2">{c.captain_name}</div>
                        <div className="space-y-2">
                          {["punctuality", "skill", "attitude"].map((dim) => (
                            <div key={dim} className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground w-24">{t(`play.${dim}`)}</span>
                              <div className="flex gap-1">
                                {[1,2,3,4,5].map((star) => (
                                  <button
                                    key={star}
                                    type="button"
                                    onClick={() => setRatings(prev => ({
                                      ...prev,
                                      [c.captain_id]: { ...(prev[c.captain_id] || {}), [dim]: star }
                                    }))}
                                    className="p-0.5"
                                  >
                                    <Star
                                      className={`h-4 w-4 ${
                                        star <= ((ratings[c.captain_id] || {})[dim] || 0)
                                          ? "fill-volt text-volt"
                                          : "text-muted-foreground/40"
                                      }`}
                                    />
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  <button
                    onClick={handleRateSubmit}
                    disabled={ratingsLoading || Object.keys(ratings).length === 0}
                    className="btn-volt rounded-sm px-6 py-3 text-xs w-full disabled:opacity-40"
                  >
                    {ratingsLoading ? t("common.loading") : t("play.submit_ratings")}
                  </button>
                </div>
              )}
            </div>
          )}

          {isPast && (isConfirmed || isCompleted) && !myClaim && !isHost && (
            <div className="border border-border/30 bg-card p-5 text-center">
              <p className="text-sm text-muted-foreground">{t("play.ratings_open_after")}</p>
            </div>
          )}
        </div>

        {isConfirmed && session.chat_id && (
          <div className="mt-6 border border-volt/20 bg-card p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading text-xl tracking-tight">{t("play.team_chat")}</div>
              {showChat ? (
                <button onClick={() => setShowChat(false)} className="text-[10px] tracking-widest uppercase text-muted-foreground hover:text-foreground">
                  {t("common.close")}
                </button>
              ) : (
                <button onClick={() => setShowChat(true)} className="text-[10px] tracking-widest uppercase text-volt">
                  {t("common.open")}
                </button>
              )}
            </div>
            {showChat && <PlayGroupChatInline sessionId={sessionId} chatId={session.chat_id} />}
          </div>
        )}
      </div>
    </div>
  );
}

function PlayGroupChatInline({ sessionId }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { socket, joinPlay, leavePlay } = useSocket();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const chatRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const forceScrollRef = useRef(false);

  const loadMessages = () => {
    api.get(`/play/${sessionId}/chat`).then(r => setMessages(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!sessionId) return;
    forceScrollRef.current = true;
    stickToBottomRef.current = true;
    loadMessages();
    joinPlay(sessionId);
    return () => {
      leavePlay(sessionId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (!socket) return;
    const handler = (msg) => {
      setMessages((prev) => [...prev, msg]);
    };
    socket.on("play:new_message", handler);
    return () => socket.off("play:new_message", handler);
  }, [socket]);

  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;
    if (forceScrollRef.current) {
      forceScrollRef.current = false;
      el.scrollTop = el.scrollHeight;
      return;
    }
    if (!stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const onScrollMessages = () => {
    const el = chatRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 120;
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (!text.trim() || !socket) return;
    socket.emit("play:message", { sessionId, text });
    setText("");
  };

  return (
    <div>
      <div ref={chatRef} onScroll={onScrollMessages} className="max-h-64 overflow-y-auto space-y-2 mb-3">
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground/60 text-center py-4">{t("play.no_messages")}</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender_id === user?.id ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-3 py-2 text-sm ${m.sender_id === user?.id ? "bg-volt text-primary-foreground" : "bg-background text-muted-foreground/80 border border-border"}`}>
                {m.sender_id !== user?.id && <div className="text-[10px] text-muted-foreground mb-0.5">{m.sender_name}</div>}
                <p>{m.text}</p>
              </div>
            </div>
          ))
        )}
      </div>
      <form onSubmit={handleSend} className="flex gap-2">
        <input
          type="text"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={t("play.chat_placeholder")}
          className="flex-1 bg-background border border-input px-3 py-2 text-sm focus:border-volt focus:outline-none placeholder:text-muted-foreground/60"
        />
        <button type="submit" disabled={!text.trim()} className="btn-volt rounded-sm px-4 py-2 text-xs disabled:opacity-40">
          {t("common.send")}
        </button>
      </form>
    </div>
  );
}
